import React from 'react'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import Button from '../../common/form/inputs/Button'
import type { PersonDto } from '@/api/person/models/PersonDto'
import type { CsPersonSearchResultDto } from '@/api/person/models/searchCs/SearchPersonsCsDtos'
import type { PersonNetworkImportStatusSummaryDto } from '@/api/person/PersonApi'
import ProfilePic from '../../common/person/ProfilePic'
import LinkedInLink from '../../../components/LinkedInLink'
import { formatPersonName } from '@/utils/personUtils'
import type { LookupNote, PersonSource } from '../types'
import LinkedInMatchesList from './LinkedInMatchesList'

type Props = {
    sourceChoice: PersonSource
    onSourceChange: (nextSource: PersonSource) => void
    isActiveCall: boolean
    sessionPerson: PersonDto | null
    linkedinUrl: string
    onLinkedinUrlChange: (nextUrl: string) => void
    onLinkedInLookup: (event: React.FormEvent) => void
    onFindRandom: () => void
    isSearching: boolean
    isFindingRandom: boolean
    lookupNote: LookupNote | null
    csMatches: CsPersonSearchResultDto[]
    selectedCsPersonId: string | null
    onSelectMatch: (person: CsPersonSearchResultDto) => void
    csNetworkImportStatuses: Record<string, PersonNetworkImportStatusSummaryDto>
    isLoadingNetworkStatuses: boolean
    csNetworkConnectionCounts: Record<string, number>
    isLoadingNetworkConnectionCounts: boolean
}

export default function ProfileSourceCard({
    sourceChoice,
    onSourceChange,
    isActiveCall,
    sessionPerson,
    linkedinUrl,
    onLinkedinUrlChange,
    onLinkedInLookup,
    onFindRandom,
    isSearching,
    isFindingRandom,
    lookupNote,
    csMatches,
    selectedCsPersonId,
    onSelectMatch,
    csNetworkImportStatuses,
    isLoadingNetworkStatuses,
    csNetworkConnectionCounts,
    isLoadingNetworkConnectionCounts,
}: Props): React.JSX.Element {
    return (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] space-y-4">
            <div>
                <h2 className="text-lg font-semibold text-foreground">
                    Pick a profile source
                </h2>
                <p className="text-sm text-muted-foreground">
                    Use your attached person or supply a LinkedIn URL.
                </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <label
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-left ${
                        sourceChoice === 'user'
                            ? 'border-primary bg-secondary/40'
                            : 'border-border bg-white'
                    }`}
                >
                    <input
                        type="radio"
                        name="profile-source"
                        value="user"
                        checked={sourceChoice === 'user'}
                        onChange={() => onSourceChange('user')}
                        disabled={isActiveCall}
                        className="mt-1"
                    />
                    <div>
                        <p className="text-sm font-semibold">Use my profile</p>
                        <p className="text-xs text-muted-foreground">
                            Uses the person attached to your user.
                        </p>
                    </div>
                </label>
                <label
                    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-left ${
                        sourceChoice === 'linkedin'
                            ? 'border-primary bg-secondary/40'
                            : 'border-border bg-white'
                    }`}
                >
                    <input
                        type="radio"
                        name="profile-source"
                        value="linkedin"
                        checked={sourceChoice === 'linkedin'}
                        onChange={() => onSourceChange('linkedin')}
                        disabled={isActiveCall}
                        className="mt-1"
                    />
                    <div>
                        <p className="text-sm font-semibold">
                            Use a LinkedIn URL
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Find a cold storage profile to pass into Kit.
                        </p>
                    </div>
                </label>
            </div>

            {sourceChoice === 'user' ? (
                <div className="space-y-3">
                    {sessionPerson ? (
                        <div className="flex items-center gap-3 rounded-2xl border border-border bg-secondary/40 p-4">
                            <ProfilePic
                                src={sessionPerson.linkedInProfilePicUrl}
                                size={48}
                                alt={formatPersonName(sessionPerson)}
                            />
                            <div className="flex-1">
                                <p className="text-sm font-semibold">
                                    {formatPersonName(sessionPerson)}
                                </p>
                                {sessionPerson.headline ? (
                                    <p className="text-xs text-muted-foreground">
                                        {sessionPerson.headline}
                                    </p>
                                ) : null}
                                {sessionPerson.linkedInUrl ? (
                                    <LinkedInLink
                                        url={sessionPerson.linkedInUrl}
                                        ariaLabel="Open LinkedIn profile"
                                    />
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <Note type={NoteType.WARNING}>
                            No person is attached to your user yet. You can
                            start without a profile or use a LinkedIn URL.
                        </Note>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    <form
                        onSubmit={onLinkedInLookup}
                        className="flex flex-col gap-3 sm:flex-row sm:items-end"
                    >
                        <div className="flex-1">
                            <label
                                htmlFor="kit-test-linkedin"
                                className="block text-sm font-semibold text-foreground"
                            >
                                LinkedIn URL
                            </label>
                            <input
                                id="kit-test-linkedin"
                                className="form-input mt-1 w-full"
                                placeholder="https://www.linkedin.com/in/your-profile"
                                value={linkedinUrl}
                                onChange={(event) =>
                                    onLinkedinUrlChange(event.target.value)
                                }
                                disabled={
                                    isActiveCall ||
                                    isSearching ||
                                    isFindingRandom
                                }
                            />
                        </div>
                        <div className="flex w-full gap-2 sm:w-auto">
                            <Button
                                type="submit"
                                style="secondary"
                                enabled={
                                    !isActiveCall &&
                                    !isSearching &&
                                    !isFindingRandom
                                }
                                classes="w-full justify-center sm:w-auto"
                            >
                                {isSearching ? 'Searching...' : 'Find profile'}
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                enabled={
                                    !isActiveCall &&
                                    !isSearching &&
                                    !isFindingRandom
                                }
                                classes="w-full justify-center sm:w-auto"
                                onClick={onFindRandom}
                            >
                                {isFindingRandom ? 'Finding...' : 'Find Random'}
                            </Button>
                        </div>
                    </form>

                    {lookupNote ? (
                        <Note type={lookupNote.type}>{lookupNote.message}</Note>
                    ) : null}

                    {csMatches.length > 0 ? (
                        <LinkedInMatchesList
                            csMatches={csMatches}
                            selectedCsPersonId={selectedCsPersonId}
                            onSelectMatch={onSelectMatch}
                            isActiveCall={isActiveCall}
                            csNetworkImportStatuses={csNetworkImportStatuses}
                            isLoadingNetworkStatuses={isLoadingNetworkStatuses}
                            csNetworkConnectionCounts={
                                csNetworkConnectionCounts
                            }
                            isLoadingNetworkConnectionCounts={
                                isLoadingNetworkConnectionCounts
                            }
                        />
                    ) : null}
                </div>
            )}
        </div>
    )
}
